import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, LayoutDashboard, ScanSearch, FileText, Radio, Settings, LogOut, CreditCard, Megaphone, BarChart3, Menu, X } from "lucide-react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/app/gap", label: "Gap Analysis", icon: ScanSearch, id: "gap" },
  { to: "/app/policies", label: "Policy Generator", icon: FileText, id: "policies" },
  { to: "/app/evidence", label: "Evidence Collector", icon: Radio, id: "evidence" },
  { to: "/app/outreach", label: "Outreach", icon: Megaphone, id: "outreach" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, id: "analytics", adminOnly: true },
  { to: "/app/billing", label: "Billing", icon: CreditCard, id: "billing" },
  { to: "/app/settings", label: "Org Settings", icon: Settings, id: "settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => !n.adminOnly || user?.role === "admin");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-brand text-white flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-brand-accent" />
          <span className="font-display font-black tracking-tight">CMMC Shield</span>
        </div>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu">
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile overlay */}
      {open && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setOpen(false)} data-testid="mobile-overlay" />}

      <aside
        data-testid="sidebar"
        className={`w-64 bg-brand text-slate-300 flex flex-col fixed h-full z-50 transition-transform duration-200
          ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className="px-6 py-6 border-b border-slate-700/60 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-7 w-7 text-brand-accent" />
            <div>
              <div className="font-display font-black text-white text-lg leading-none tracking-tight">CMMC Shield</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Readiness Platform</div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden text-slate-400" aria-label="Close menu"><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors border-l-2 ${
                  isActive
                    ? "bg-slate-800/60 text-white border-brand-accent"
                    : "border-transparent hover:bg-slate-800/30 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-6 py-4 border-t border-slate-700/60">
          <div className="text-xs text-slate-400 truncate mb-2" data-testid="sidebar-user">{user?.email}</div>
          <button
            onClick={() => { logout(); navigate("/login"); }}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0">{children}</main>
    </div>
  );
}
